import React, { useRef } from 'react';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import { Modal, Row, Col, Button } from 'antd';
import * as Yup from 'yup';

import Input from 'components/formik/Input';
import FormItem from 'components/formik/FormItem';
import SubmitButton from 'components/formik/SubmitButton';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BaseAlert from 'components/wrappers/BaseAlert';
import { updateBillingInformation } from 'redux/reducers/accountSlice';
import { updateCorporateTABillingInformation } from 'redux/reducers/recruiterSlice';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { ProfileCorporateBillingInfoModel } from 'api/backofficeApiClient';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

type Props = {
  onClose: () => void;
  onEdit: () => Promise<void>;
  companyId?: string;
  billingInfo?: ProfileCorporateBillingInfoModel;
};

export interface BillingInfoFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const validationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .max(120, 'First name should be 120 characters maximum')
    .required('Field is required'),
  lastName: Yup.string()
    .trim()
    .max(120, 'Last name should be 120 characters maximum')
    .required('Field is required'),
  email: Yup.string()
    .email('Email format is invalid')
    .required('Field is required')
    .max(120, 'Email should be 120 characters maximum'),
  phone: Yup.string()
    .trim()
    .required('Field is required')
    .max(120, 'Phone number should be 120 characters maximum'),
});

export default function EditBillingInfoModal(props: Props) {
  const { companyId, billingInfo, onClose, onEdit } = props;
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const formRef: any = useRef();
  const initialValues: BillingInfoFormValues = {
    firstName: billingInfo?.firstName || '',
    lastName: billingInfo?.lastName || '',
    email: billingInfo?.email || '',
    phone: billingInfo?.phoneNumber || '',
  };

  const handleSubmit = async (values: BillingInfoFormValues) => {
    const { firstName, lastName, email, phone } = values;

    const billingInfoData: ProfileCorporateBillingInfoModel = {
      firstName,
      lastName,
      email,
      phoneNumber: phone,
    };

    const correctAction = isAdmin
      ? updateCorporateTABillingInformation
      : updateBillingInformation;
    const requestData = {
      companyId: isAdmin ? companyId : undefined,
      billingInfo: billingInfoData,
    };
    const result = await dispatch(correctAction(requestData));

    if (correctAction.fulfilled.match(result)) {
      await onEdit();

      BaseAlert.success('Billing information saved.');
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleModalCancel = () => {
    if (formRef.current?.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  return (
    <BaseModal
      title="Edit billing information"
      maxWidth={888}
      open={true}
      onCancel={handleModalCancel}
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <Row gutter={48} align="middle">
              <Col xs={24} md={7}>
                <div className="modal-title">Billing information</div>
              </Col>
            </Row>

            <Row gutter={40}>
              <Col xs={24} md={12}>
                <Row gutter={20}>
                  <Col xs={24} md={12}>
                    <FormItem
                      label="A/P first name *"
                      name="firstName"
                      data-id="billingInfoFirstName"
                    >
                      <Input
                        placeholder="Enter A/P first name"
                        name="firstName"
                      />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem
                      label="A/P last name *"
                      name="lastName"
                      data-id="billingInfoLastName"
                    >
                      <Input
                        placeholder="Enter A/P last name"
                        name="lastName"
                      />
                    </FormItem>
                  </Col>
                </Row>
              </Col>

              <Col xs={24} md={12}>
                <Row gutter={20}>
                  <Col xs={24} md={12}>
                    <FormItem
                      label="A/P email *"
                      name="email"
                      data-id="billingInfoEmail"
                    >
                      <Input placeholder="Enter A/P email" name="email" />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem
                      label="A/P phone number *"
                      name="phone"
                      data-id="billingInfoPhone"
                    >
                      <Input name="phone" placeholder="Enter phone number" />
                    </FormItem>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>

          <BaseModalFooter>
            <Button onClick={handleModalCancel}>Cancel</Button>

            <SubmitButton type="primary">Save</SubmitButton>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
